// Inline SVG icons. Same set as the React version, ported to Solid.

import type { JSX } from "solid-js";

type IconProps = { size?: number; color?: string; filled?: boolean };
type IconFn = (p?: IconProps) => JSX.Element;

const def = (size = 16, color = "#b8923f", filled = false) => ({ size, color, filled });

export const Icons: Record<string, IconFn> = {
  Crown: (p) => {
    const { size, color } = { ...def(14, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 20 16" fill="none">
        <path d="M2 4l3.5 3 4.5-6 4.5 6 3.5-3-1.5 10h-13L2 4z" fill={color} stroke={color} stroke-width="1" stroke-linejoin="round" />
        <circle cx="10" cy="14" r="0.8" fill="#fff" opacity="0.6" />
      </svg>
    );
  },
  Sparkle: (p) => {
    const { size, color } = { ...def(14, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
        <path d="M10 1 L11.5 8.5 L19 10 L11.5 11.5 L10 19 L8.5 11.5 L1 10 L8.5 8.5 Z" fill={color} />
        <circle cx="16" cy="4" r="1" fill={color} opacity="0.6" />
        <circle cx="3" cy="16" r="0.7" fill={color} opacity="0.4" />
      </svg>
    );
  },
  Check: (p) => {
    const { size, color } = { ...def(18, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke={color} stroke-width="1.5" />
        <path d="M7.5 12.5L10.5 15.5L16.5 9" stroke={color} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    );
  },
  Bulb: (p) => {
    const { size, color } = { ...def(20, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <path d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.5c1 1 1.5 2 1.5 3.5h5c0-1.5.5-2.5 1.5-3.5A6 6 0 0012 3z" stroke={color} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M12 3v-1M19 7l0.7-0.7M5 7l-0.7-0.7M21 14h1M3 14h-1" stroke={color} stroke-width="1.5" stroke-linecap="round" />
      </svg>
    );
  },
  Profile: (p) => {
    const { size, color, filled } = { ...def(26, "#999", false), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 26 26" fill="none">
        <circle cx="13" cy="9" r="4" stroke={color} stroke-width={filled ? 0 : 1.6} fill={filled ? color : "none"} />
        <path d="M4 22c1.5-4 5-6 9-6s7.5 2 9 6" stroke={color} stroke-width="1.6" stroke-linecap="round" fill={filled ? color : "none"} fill-opacity={filled ? 0.15 : 0} />
      </svg>
    );
  },
  SlotIcon: (p) => {
    const { size, color } = { ...def(26, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 26 26" fill="none">
        <rect x="3" y="6" width="20" height="16" rx="3" stroke={color} stroke-width="1.6" />
        <rect x="6" y="10" width="3" height="6" rx="1" fill={color} />
        <rect x="11.5" y="10" width="3" height="6" rx="1" fill={color} />
        <rect x="17" y="10" width="3" height="6" rx="1" fill={color} />
        <path d="M9 6V4M17 6V4M11 22v2M15 22v2" stroke={color} stroke-width="1.6" stroke-linecap="round" />
      </svg>
    );
  },
  Bookmark: (p) => {
    const { size, color, filled } = { ...def(26, "#999", false), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 26 26" fill="none">
        <path d="M6 4h14v18l-7-5-7 5V4z" stroke={color} stroke-width="1.6" fill={filled ? color : "none"} stroke-linejoin="round" />
      </svg>
    );
  },
  Trash: (p) => {
    const { size, color } = { ...def(18, "#c33"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
        <path d="M3 5h14M7 5V3h6v2M5 5l1 13h8l1-13M9 9v6M11 9v6" stroke={color} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    );
  },
  Share: (p) => {
    const { size, color } = { ...def(18, "#1a1a1a"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
        <path d="M10 13V3M10 3l-3 3M10 3l3 3M4 11v5a1 1 0 001 1h10a1 1 0 001-1v-5" stroke={color} stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    );
  },
  Close: (p) => {
    const { size, color } = { ...def(22, "#1a1a1a"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 22 22" fill="none">
        <path d="M6 6L16 16M16 6L6 16" stroke={color} stroke-width="1.8" stroke-linecap="round" />
      </svg>
    );
  },
  Chevron: (p) => {
    const { size, color } = { ...def(14, "#999"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
        <path d="M5 3l5 4-5 4" stroke={color} stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" fill="none" />
      </svg>
    );
  },
  Search: (p) => {
    const { size, color } = { ...def(18, "#999"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 18 18" fill="none">
        <circle cx="8" cy="8" r="5.5" stroke={color} stroke-width="1.6" />
        <path d="M12.5 12.5l3 3" stroke={color} stroke-width="1.6" stroke-linecap="round" />
      </svg>
    );
  },
  Globe: (p) => {
    const { size, color } = { ...def(14, "#1a1a1a"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="7" stroke={color} stroke-width="1.4" />
        <path d="M2 9h14M9 2c2 2.5 3 5 3 7s-1 4.5-3 7c-2-2.5-3-5-3-7s1-4.5 3-7z" stroke={color} stroke-width="1.2" />
      </svg>
    );
  },
  Plus: (p) => {
    const { size, color } = { ...def(16, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <path d="M8 3v10M3 8h10" stroke={color} stroke-width="1.8" stroke-linecap="round" />
      </svg>
    );
  },
  Clock: (p) => {
    const { size, color } = { ...def(14, "#8a6f1f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke={color} stroke-width="1.6" />
        <path d="M8 4.8V8l2.2 1.4" stroke={color} stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    );
  },
  Star: (p) => {
    const { size, color } = { ...def(14, "#b8923f"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
        {/* Telegram Stars wordmark feel: solid 5-point star with a soft
            inner highlight, not a wireframe. */}
        <path
          d="M8 1.6l1.95 3.95 4.36.63-3.16 3.07.75 4.34L8 11.55l-3.9 2.04.74-4.34L1.7 6.18l4.36-.63L8 1.6z"
          fill={color}
          stroke={color}
          stroke-width="0.6"
          stroke-linejoin="round"
        />
        <path
          d="M8 3.6L9.3 6.25l2.9.42-2.1 2.05.5 2.9L8 10.25"
          fill="rgba(255,255,255,0.32)"
        />
      </svg>
    );
  },
  SoundOn: (p) => {
    const { size, color } = { ...def(18, "#1a1a1a"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
        <path d="M3 7h3l4-3v12l-4-3H3V7z" stroke={color} stroke-width="1.5" stroke-linejoin="round" fill={color} fill-opacity="0.18" />
        <path d="M13.5 7c1 1 1 5 0 6M16 5c1.8 2 1.8 8 0 10" stroke={color} stroke-width="1.5" stroke-linecap="round" fill="none" />
      </svg>
    );
  },
  SoundOff: (p) => {
    const { size, color } = { ...def(18, "#1a1a1a"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
        <path d="M3 7h3l4-3v12l-4-3H3V7z" stroke={color} stroke-width="1.5" stroke-linejoin="round" fill={color} fill-opacity="0.18" />
        <path d="M14 8l4 4M18 8l-4 4" stroke={color} stroke-width="1.6" stroke-linecap="round" />
      </svg>
    );
  },
  Copy: (p) => {
    const { size, color } = { ...def(16, "#1a1a1a"), ...p };
    return (
      <svg width={size} height={size} viewBox="0 0 18 18" fill="none">
        <rect x="3" y="3" width="10" height="12" rx="1.5" stroke={color} stroke-width="1.5" />
        <path d="M6 3V2a1 1 0 011-1h7a1 1 0 011 1v10a1 1 0 01-1 1h-1" stroke={color} stroke-width="1.5" />
      </svg>
    );
  },
};
